import axios from 'axios';
import { useState, useEffect } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { getConfig } from '../providers/ConfigProvider';

/**
 * Custom hook for making authenticated API requests.
 * @param url - The API endpoint path (appended to API_BASE_URL from config).
 * @param method - The HTTP method.
 * @param payload - Optional request body.
 * @param enabled - When false, the request is skipped entirely (defaults to true).
 */
export function useAxios<T>(
  url: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  payload?: unknown,
  enabled: boolean = true,
) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(enabled);
  const { authState } = useOktaAuth();

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    const headers: Record<string, string> = {};
    if (authState && authState.isAuthenticated) {
      headers['Authorization'] = 'Bearer ' + authState.accessToken?.accessToken;
    }

    axios
      .request<T>({
        data: payload,
        method,
        url: getConfig().API_BASE_URL + url,
        headers,
      })
      .then((response) => setData(response.data))
      .catch((error) => setError(error.message))
      .finally(() => setLoading(false));
  }, []);

  return { data, error, loading };
}
